import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { CouponTable, Coupon } from "../components/coupons/CouponTable";
import { AddEditCouponDialog } from "../components/coupons/AddEditCouponDialog";
import { DeleteCouponDialog } from "../components/coupons/DeleteCouponDialog";
import { useToast } from "../components/ui/ToastContainer";
import { couponService } from "../services/couponService";
import { parseApiError } from "../utils/errorHandler";

export default function CouponsPage() {
  const { showSuccess, showError } = useToast();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCoupons, setTotalCoupons] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");

  // Dialog states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchCoupons = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
      };

      // Add filters only if they're not "all"
      if (statusFilter !== "all") {
        params.isActive = statusFilter === "active";
      }

      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }

      const response = await couponService.getAllCoupons(params);
      setCoupons((response.data || []) as unknown as Coupon[]);
      setTotalCoupons(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch coupons:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Failed to Load Coupons");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, debouncedSearchTerm, showError]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleEditClick = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setIsDeleteOpen(true);
  };

  const handleViewClick = (coupon: Coupon) => {
    // For now, open edit dialog in view mode
    setSelectedCoupon(coupon);
    setIsEditOpen(true);
  };

  const handleToggleStatus = async (coupon: Coupon) => {
    try {
      await couponService.toggleCouponStatus(coupon._id);
      showSuccess(
        `Coupon ${coupon.isActive ? "deactivated" : "activated"} successfully`,
        "Success"
      );
      fetchCoupons();
    } catch (error) {
      console.error("Failed to toggle coupon status:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Status Update Failed");
    }
  };

  // Handler functions for filters and pagination
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Debouncing is handled by useEffect above
  };

  const handleStatusFilterChange = (status: "all" | "active" | "inactive") => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page on page size change
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6">
        <CouponTable
          coupons={coupons}
          description="Create and manage discount codes for your courses."
          isLoading={isLoading}
          totalCoupons={totalCoupons}
          currentPage={currentPage}
          pageSize={pageSize}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onSearchChange={handleSearchChange}
          onStatusFilterChange={handleStatusFilterChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onAddClick={() => {
            setSelectedCoupon(null);
            setIsAddOpen(true);
          }}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onViewClick={handleViewClick}
          onToggleStatus={handleToggleStatus}
        />

        {/* Add/Edit Dialog */}
        <AddEditCouponDialog
          isOpen={isAddOpen || isEditOpen}
          onClose={() => {
            setIsAddOpen(false);
            setIsEditOpen(false);
            setSelectedCoupon(null);
          }}
          onSuccess={fetchCoupons}
          coupon={selectedCoupon}
        />

        {/* Delete Dialog */}
        <DeleteCouponDialog
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setSelectedCoupon(null);
          }}
          onSuccess={fetchCoupons}
          coupon={selectedCoupon}
        />
      </div>
    </DashboardLayout>
  );
}
